// "My P&L" — profit an officer facilitated vs what they took (Doug 2026-10-07).
// Numbers come from public.officer_scorecard / public.officer_scorecard_roster; the server decides who sees what
// (officer = self only, council = everyone + roster). Renders nothing for someone with no roles and no comp.
import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import {
  fetchOfficerScorecard, fetchOfficerRoster, scorecardWindow, fmtUsd, fmtRatio, fmtShare,
} from '@bricksOS/finance/logic/officerScorecard.js';

const MODES = [
  { id: 'ytd', label: 'YTD' },
  { id: 'month', label: 'This month' },
  { id: 'weeks', label: '12 weeks' },
];

function Figure({ label, value, sub }) {
  return (
    <div className="min-w-0">
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-2xl font-semibold text-white tabular-nums">{value}</div>
      {sub ? <div className="text-xs text-gray-500 tabular-nums">{sub}</div> : null}
    </div>
  );
}

function Lines({ title, items }) {
  if (!items.length) return null;
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">{title}</div>
      {items.map((x) => (
        <div key={x.type} className="flex justify-between text-sm py-0.5">
          <span className="text-gray-300 truncate pr-3">{x.label}</span>
          <span className="text-white tabular-nums">{fmtUsd(x.amount)}</span>
        </div>
      ))}
    </div>
  );
}

export function OfficerScorecardView({ personId = null, personName = null, onBack = null }) {
  const [mode, setMode] = useState('ytd');
  const [state, setState] = useState({ loading: true });
  const win = useMemo(() => scorecardWindow(mode), [mode]);

  useEffect(() => {
    let live = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchOfficerScorecard(supabase, { person: personId, ...win })
      .then((r) => live && setState({ loading: false, ...r }))
      .catch((error) => live && setState({ loading: false, error }));
    return () => { live = false; };
  }, [personId, win]);

  if (state.error) return <div className="text-sm text-red-400">P&L could not load: {state.error.message}</div>;
  if (state.denied) return <div className="text-sm text-gray-400">You can see only your own P&L.</div>;
  const sc = state.scorecard;
  if (!sc && state.loading) return <div className="text-sm text-gray-500">Loading P&L…</div>;
  if (!sc) return null;

  const t = sc.totals;
  return (
    <div className={state.loading ? 'opacity-60' : ''}>
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="text-white font-semibold truncate">
          {onBack ? <button onClick={onBack} className="text-gray-400 hover:text-white mr-2">←</button> : null}
          {personName || 'My P&L'}
        </div>
        <div className="flex gap-1">
          {MODES.map((m) => (
            <button
              key={m.id}
              onClick={() => setMode(m.id)}
              className={`px-2 py-1 text-xs rounded ${mode === m.id ? 'bg-vegas-gold text-black' : 'text-gray-400 hover:text-white'}`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        <Figure label="Profit facilitated" value={fmtUsd(t.facilitated_cash)} sub={`accrual ${fmtUsd(t.facilitated_accrual)}`} />
        <Figure label="Taken" value={fmtUsd(t.take)} />
        <Figure label="Facilitated : taken" value={fmtRatio(t.ratio_cash)} />
        <Figure label="Paid out" value={fmtUsd(t.paid)} />
      </div>

      {sc.accounts.length ? (
        <table className="w-full text-sm mb-5">
          <thead>
            <tr className="text-xs uppercase tracking-wide text-gray-500 text-left">
              <th className="font-normal pb-1">Account</th>
              <th className="font-normal pb-1">Role</th>
              <th className="font-normal pb-1 text-right">Profit</th>
              <th className="font-normal pb-1 text-right">Credit</th>
              <th className="font-normal pb-1 text-right">Yours</th>
            </tr>
          </thead>
          <tbody>
            {sc.accounts.map((a) => (
              <tr key={a.account_id} className="text-gray-300">
                <td className="py-0.5 pr-2 truncate max-w-[14rem]">{a.account}</td>
                <td className="py-0.5 pr-2 text-gray-500">{a.roles}</td>
                <td className="py-0.5 text-right tabular-nums">{fmtUsd(a.contribution_cash)}</td>
                <td className="py-0.5 text-right tabular-nums text-gray-500">{fmtShare(a.credit_share)}</td>
                <td className="py-0.5 text-right tabular-nums text-white">{fmtUsd(a.facilitated_cash)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      <div className="grid md:grid-cols-2 gap-5 mb-5">
        <Lines title="Taken" items={sc.take} />
        <Lines title="Paid out" items={sc.paid} />
      </div>
      <Lines title="Credited on a basis under review" items={sc.flags} />

      {sc.trend.length > 1 ? (
        <table className="w-full text-sm mt-5">
          <thead>
            <tr className="text-xs uppercase tracking-wide text-gray-500 text-left">
              <th className="font-normal pb-1">{win.grain === 'week' ? 'Week' : 'Month'}</th>
              <th className="font-normal pb-1 text-right">Facilitated</th>
              <th className="font-normal pb-1 text-right">Taken</th>
              <th className="font-normal pb-1 text-right">Ratio</th>
            </tr>
          </thead>
          <tbody>
            {sc.trend.map((p) => (
              <tr key={p.period_start} className="text-gray-300">
                <td className="py-0.5">{p.label}</td>
                <td className="py-0.5 text-right tabular-nums">{fmtUsd(p.facilitated_cash)}</td>
                <td className="py-0.5 text-right tabular-nums">{fmtUsd(p.take)}</td>
                <td className="py-0.5 text-right tabular-nums text-gray-500">{fmtRatio(p.ratio_cash)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      <div className="text-xs text-gray-600 mt-4">
        Profit = account net before leadership pay. Credit split per the leadership policy.
        {sc.asOf ? ` Account figures as of ${new Date(sc.asOf).toLocaleString('en-US', { timeZone: 'America/New_York' })}.` : ''}
      </div>
    </div>
  );
}

function OfficerRoster({ roster, onOpen }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-xs uppercase tracking-wide text-gray-500 text-left">
          <th className="font-normal pb-1">Officer · YTD</th>
          <th className="font-normal pb-1 text-right">Facilitated</th>
          <th className="font-normal pb-1 text-right">Taken</th>
          <th className="font-normal pb-1 text-right">Ratio</th>
        </tr>
      </thead>
      <tbody>
        {roster.map((r) => (
          <tr
            key={r.valet_id}
            className="text-gray-300 cursor-pointer hover:text-white"
            onClick={() => onOpen(r)}
          >
            <td className="py-0.5 pr-2">{r.person_name} <span className="text-gray-600">{r.roles || ''}</span></td>
            <td className="py-0.5 text-right tabular-nums">{fmtUsd(r.facilitated_cash)}</td>
            <td className="py-0.5 text-right tabular-nums">{fmtUsd(r.take)}</td>
            <td className="py-0.5 text-right tabular-nums text-gray-500">{fmtRatio(r.ratio_cash)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Profile section: my P&L, plus every officer when the server returns the roster (council/finance).
 * Shown only to someone who leads an account or is council — a valet with no roles sees nothing here.
 */
export default function OfficerPnlPanel() {
  const [view, setView] = useState('mine');
  const [other, setOther] = useState(null);
  const [roster, setRoster] = useState(null);
  const [leadsAccounts, setLeadsAccounts] = useState(false);

  useEffect(() => {
    let live = true;
    const { start, end } = scorecardWindow('ytd');
    fetchOfficerRoster(supabase, { start, end })
      .then((r) => live && !r.denied && setRoster(r.roster || []))
      .catch(() => {});
    fetchOfficerScorecard(supabase, { start, end, grain: 'total' })
      .then((r) => live && setLeadsAccounts(Boolean(r.scorecard?.accounts?.length)))
      .catch(() => {});
    return () => { live = false; };
  }, []);

  if (!leadsAccounts && !roster) return null;
  return (
    <section id="my-pnl" className="bg-gray-900/60 rounded-xl p-6 mb-8">
      {roster ? (
        <div className="flex gap-3 mb-4 text-sm">
          <button onClick={() => { setView('mine'); setOther(null); }} className={view === 'mine' ? 'text-vegas-gold' : 'text-gray-500 hover:text-white'}>My P&L</button>
          <button onClick={() => setView('roster')} className={view === 'roster' ? 'text-vegas-gold' : 'text-gray-500 hover:text-white'}>Officers</button>
        </div>
      ) : null}
      {view === 'mine' ? <OfficerScorecardView /> : null}
      {view === 'roster' && roster && !other ? <OfficerRoster roster={roster} onOpen={setOther} /> : null}
      {view === 'roster' && other ? (
        <OfficerScorecardView personId={other.valet_id} personName={other.person_name} onBack={() => setOther(null)} />
      ) : null}
    </section>
  );
}
